// Reference notation helpers — Q5 from DESIGN-DECISIONS-2026-05-01.md
//
// Cascade per entity:
//   1. Prefer human-readable identifier where present (Nsofar SR ID, name)
//   2. Fall back to typed prefix slice (SR-1a2bc, P-9f3cd, C-22b8e, A-77eef)
//
// 5 hex chars = ~1M unique values per type. ~0.2% collision at 2K patients/tenant.
// Drop the leading `#` — `SR-1a2b` not `SR #1a2b`.

const PREFIX_SR      = "SR";
const PREFIX_PATIENT = "P";
const PREFIX_CARER   = "C";
const PREFIX_AGENT   = "A";

/** Last 5 hex chars of an ObjectId, lowercased. */
function shortId(id: string | null | undefined): string {
  if (!id) return "????";
  return id.slice(-5).toLowerCase();
}

interface SrLike {
  _id: string;
  nsofar_sr_id?: string | null;
}

/** Service request reference. Prefers Nsofar SR ID when present. */
export function formatSrRef(sr: SrLike): string {
  if (sr.nsofar_sr_id) return sr.nsofar_sr_id;
  return `${PREFIX_SR}-${shortId(sr._id)}`;
}

interface NamedEntity {
  _id: string;
  full_name?: string | null;
  name?: string | null;
}

/** Patient reference. Prefers name when decryption permits (ADR-014). */
export function formatPatientRef(p: NamedEntity): string {
  return p.full_name ?? p.name ?? `${PREFIX_PATIENT}-${shortId(p._id)}`;
}

/**
 * Patient slice ONLY — always renders P-XXXXX, never the name. Use for
 * "Ref" / system-identifier columns where the column header promises a
 * stable machine identifier regardless of the role gate (ADR-014). Use
 * formatPatientRef for human-facing identifier columns.
 */
export function formatPatientSlice(p: { _id: string }): string {
  return `${PREFIX_PATIENT}-${shortId(p._id)}`;
}

/** Carer reference. Prefers name when present. */
export function formatCarerRef(c: NamedEntity): string {
  return c.full_name ?? c.name ?? `${PREFIX_CARER}-${shortId(c._id)}`;
}

/** Agent reference. Slice-only for v1 (agents may not expose names yet). */
export function formatAgentRef(id: string | null | undefined): string {
  if (!id) return "Unassigned";
  return `${PREFIX_AGENT}-${shortId(id)}`;
}

/** Standalone slice formatter for cases without a known entity type. */
export function formatGenericRef(id: string | null | undefined, prefix: string): string {
  if (!id) return "—";
  return `${prefix}-${shortId(id)}`;
}

// ── Screen-reader-friendly forms (TASK-24) ────────────────────────────────────
// `formatPatientRef`/`formatSrRef` return a typed-prefix slice (`P-9f3c`,
// `SR-1a2b`) when no name/Nsofar-id is available — screen readers pronounce
// those letter-by-letter, often incorrectly ("P dash nine eff three see").
// The verbose forms below substitute the typed prefix with the word
// "reference", yielding labels like "View patient with reference 9f3c"
// rather than "View patient P-9f3c". Use these only in `aria-label`s — keep
// the compact `format*Ref` for visible Ref columns.

/** Verbose patient label for aria-label. Falls back to "with reference XXXX". */
export function verbalizePatientRef(p: NamedEntity): string {
  return p.full_name ?? p.name ?? `with reference ${shortId(p._id)}`;
}

/** Verbose SR label for aria-label. Falls back to "reference XXXX". */
export function verbalizeSrRef(sr: SrLike): string {
  if (sr.nsofar_sr_id) return sr.nsofar_sr_id;
  return `reference ${shortId(sr._id)}`;
}
